import {
    AlertTriangle,
    BarChart3,
    Building2,
    Clock,
    DollarSign,
    FileText,
    Home,
    TrendingDown,
    TrendingUp,
    Users,
    Wrench
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const RMCDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [businessSummary, setBusinessSummary] = useState(null);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [overdueSummary, setOverdueSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [detailedData, setDetailedData] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailedView, setShowDetailedView] = useState(false);

  // API 함수들을 먼저 정의
  const fetchIntegratedStats = useCallback(async (ownerId = null) => {
    try {
      let url = '/rmc/integrated-stats';
      if (ownerId) {
        url += `?owner_id=${ownerId}`;
      }
      
      const response = await api.get(url);
      console.log('API 응답:', response);
      
      if (response) {
        console.log('overview 데이터:', response.overview);
        console.log('business_summary 데이터:', response.business_summary);
        
        // 데이터가 존재하는지 확인
        if (response.overview) {
          setStats(response.overview);
          console.log('✅ overview 데이터 설정 완료');
        } else {
          console.log('⚠️ overview 데이터가 없습니다');
        }
        
        if (response.business_summary) {
          setBusinessSummary(response.business_summary);
          console.log('✅ business_summary 데이터 설정 완료');
        } else {
          console.log('⚠️ business_summary 데이터가 없습니다');
        }
        
        console.log('통합 통계 로드 성공:', response);
      } else {
        console.log('응답 데이터가 없습니다:', response);
      }
    } catch (error) {
      console.error('통합 통계 조회 오류:', error);
      throw error; // 에러를 상위로 전파
    }
  }, []);

  const fetchPropertyOwners = useCallback(async () => {
    try {
      const response = await api.get('/rmc/property-owners');
      console.log('임대사업자 목록 응답:', response);
      
      if (response && Array.isArray(response)) {
        setPropertyOwners(response);
        console.log('✅ 임대사업자 목록 설정 완료:', response.length, '개');
      } else {
        console.log('⚠️ 임대사업자 목록 데이터가 없거나 배열이 아닙니다');
        setPropertyOwners([]);
      }
    } catch (error) {
      console.error('임대사업자 목록 조회 오류:', error);
      throw error;
    }
  }, []);

  const fetchOverdueSummary = useCallback(async () => {
    try {
      const response = await api.get('/rmc/overdue-summary');
      console.log('연체 요약 응답:', response);
      
      if (response) {
        setOverdueSummary(response);
        console.log('✅ 연체 요약 설정 완료:', response);
      } else {
        console.log('⚠️ 연체 요약 데이터가 없습니다');
        setOverdueSummary({});
      }
    } catch (error) {
      console.error('연체 현황 조회 오류:', error);
      throw error;
    }
  }, []);

  const fetchBusinessSummary = useCallback(async (businessType, ownerId = null) => {
    console.log(`🚀 fetchBusinessSummary 호출: ${businessType}, ownerId: ${ownerId}`);
    try {
      let url = `/rmc/business-summary/${businessType}`;
      if (ownerId) {
        url += `?owner_id=${ownerId}`;
      }
      
      console.log(`📡 API 호출 URL: ${url}`);
      const response = await api.get(url);
      console.log(`${businessType} API 응답:`, response);
      
      if (response) {
        setBusinessSummary(prev => {
          const newSummary = {
            ...prev,
            [businessType]: response
          };
          console.log(`📊 businessSummary 업데이트:`, newSummary);
          return newSummary;
        });
        console.log(`${businessType} 요약 로드 성공:`, response);
      } else {
        console.log(`⚠️ ${businessType} 응답 데이터가 없습니다`);
      }
    } catch (error) {
      console.error(`${businessType} 요약 조회 오류:`, error);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('🔍 RMC 대시보드 초기화 시작');
        console.log('👤 현재 사용자:', user);
        setLoading(true);
        setError(null);
        await Promise.all([
          fetchIntegratedStats(),
          fetchPropertyOwners(),
          fetchOverdueSummary()
        ]);
        console.log('✅ RMC 대시보드 초기화 완료');
      } catch (error) {
        console.error('❌ 초기 데이터 로딩 실패:', error);
        setError('데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadInitialData();
    }
  }, [user]);

  // 선택된 임대사업자가 변경될 때 통계 다시 가져오기
  useEffect(() => {
    // propertyOwners가 로드된 후에만 실행
    if (propertyOwners.length > 0) {
      if (selectedOwners.length > 0) {
        const selectedOwner = propertyOwners.find(po => selectedOwners.includes(po.name));
        if (selectedOwner) {
          fetchIntegratedStats(selectedOwner.id);
        }
      } else {
        fetchIntegratedStats(); // 전체 통계
      }
    }
  }, [selectedOwners, propertyOwners]);

  useEffect(() => {
    console.log(`🔄 useEffect 실행: activeTab=${activeTab}, selectedOwners=${selectedOwners.length}, propertyOwners=${propertyOwners.length}`);
    if (activeTab !== 'overview') {
      if (selectedOwners.length > 0 && propertyOwners.length > 0) {
        const selectedOwner = propertyOwners.find(po => selectedOwners.includes(po.name));
        if (selectedOwner) {
          console.log(`👤 선택된 사업자로 API 호출: ${selectedOwner.name} (ID: ${selectedOwner.id})`);
          fetchBusinessSummary(activeTab, selectedOwner.id);
        }
      } else {
        console.log(`🌐 전체 사업자로 API 호출`);
        fetchBusinessSummary(activeTab);
      }
    } else {
      console.log(`📊 overview 탭이므로 API 호출하지 않음`);
    }
  }, [activeTab, selectedOwners, propertyOwners]);


  useEffect(() => {
    if (
      showDetailedView &&
      activeTab !== 'overview'
    ) {
      fetchDetailedData();
    }
  }, [showDetailedView, activeTab, currentPage, searchTerm, selectedOwners]);




  const fetchDetailedData = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 20,
        search: searchTerm
      });

      // 선택된 임대사업자 필터 추가
      if (selectedOwners.length > 0) {
        const selectedOwner = propertyOwners.find(po => selectedOwners.includes(po.name));
        if (selectedOwner) {
          params.append('owner_id', selectedOwner.id);
        }
      }

      const response = await api.get(`/rmc/detailed-data/${activeTab}?${params}`);
      const payload = response?.data || response;
      setDetailedData(payload?.items || []);
      setPagination(payload?.pagination || null);
      console.log('상세 데이터 로드 성공:', payload);
    } catch (error) {
      console.error('상세 데이터 조회 오류:', error);
      setDetailedData([]);
      setPagination(null);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
  };

  const toggleDetailedView = () => {
    setShowDetailedView(!showDetailedView);
    setDetailedData(null);
    setPagination(null);
    if (!showDetailedView) {
      setCurrentPage(1);
      setSearchTerm('');
    }
  };

  const handleSendNotification = async (overdueId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/rmc/actions/send-notification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overdue_id: overdueId,
          notification_type: 'SMS'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        // 데이터 새로고침
        fetchDetailedData();
      } else {
        const error = await response.json();
        alert(`오류: ${error.error}`);
      }
    } catch (error) {
      console.error('알림 발송 오류:', error);
      alert('알림 발송 중 오류가 발생했습니다.');
    }
  };

  const handleUpdateStage = async (overdueId) => {
    const newStage = prompt('새로운 연체 단계를 입력하세요 (DUE_REMINDER, D3, D15, D30, D60, D90, D100):');
    if (!newStage) return;

    const newStatus = prompt('새로운 상태를 입력하세요 (green, yellow, orange, red, black):');
    if (!newStatus) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/rmc/actions/update-overdue-stage', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overdue_id: overdueId,
          new_stage: newStage,
          new_status: newStatus,
          notes: '단계 수동 변경'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        // 데이터 새로고침
        fetchDetailedData();
      } else {
        const error = await response.json();
        alert(`오류: ${error.error}`);
      }
    } catch (error) {
      console.error('단계 업데이트 오류:', error);
      alert('단계 업데이트 중 오류가 발생했습니다.');
    }
  };

  const handleResolveOverdue = async (overdueId) => {
    const resolutionMethod = prompt('해결 방법을 입력하세요 (payment, partial_payment, agreement):');
    if (!resolutionMethod) return;

    const notes = prompt('해결 메모를 입력하세요 (선택사항):');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/rmc/actions/resolve-overdue', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overdue_id: overdueId,
          resolution_method: resolutionMethod,
          notes: notes || ''
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        // 데이터 새로고침
        fetchDetailedData();
      } else {
        const error = await response.json();
        alert(`오류: ${error.error}`);
      }
    } catch (error) {
      console.error('연체 해결 오류:', error);
      alert('연체 해결 처리 중 오류가 발생했습니다.');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', trend = null }) => (
    <div className="bg-white rounded-lg shadow p-3 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value?.toLocaleString() || 0}</p>
          {trend && (
            <div className={`flex items-center mt-1 text-xs sm:text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-full bg-${color}-100 flex-shrink-0`}>
          <Icon className={`w-4 h-4 sm:w-6 sm:h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const OverdueStageCard = ({ stage, count, color }) => {
    const stageNames = {
      'DUE_REMINDER': '납부 안내',
      'D3': '3일 연체',
      'D15': '15일 연체',
      'D30': '30일 연체',
      'D60': '60일 연체',
      'D90': '90일 연체',
      'D100': '100일+ 연체'
    };

    return (
      <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium text-${color}-800`}>
              {stageNames[stage] || stage}
            </p>
            <p className={`text-2xl font-bold text-${color}-900`}>{count}</p>
          </div>
          <AlertTriangle className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    );
  };

  const renderBusinessContent = (businessType) => {
    console.log(`🎯 renderBusinessContent 호출: ${businessType}`);
    console.log('📊 businessSummary 전체:', businessSummary);
    
    const summary = businessSummary?.[businessType];
    console.log(`📋 ${businessType} summary:`, summary);
    
    if (!summary) {
      console.log(`⚠️ ${businessType} summary가 없습니다`);
      return <div className="text-gray-500">데이터를 불러오는 중...</div>;
    }

    // summary 객체의 안전한 초기화
    const safeSummary = {
      ...summary,
      by_owner: summary.by_owner || {},
      by_stage: summary.by_stage || {},
      by_region: summary.by_region || {}
    };

    switch (businessType) {
      case 'buildings':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">총 건물 수</h4>
                <p className="text-2xl font-bold text-blue-600">{safeSummary.total_buildings || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">총 세대 수</h4>
                <p className="text-2xl font-bold text-green-600">{safeSummary.total_units || 0}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900">공실 세대</h4>
                <p className="text-2xl font-bold text-orange-600">{safeSummary.vacant_units || 0}</p>
              </div>
            </div>
            {selectedOwners.length > 0 && safeSummary.by_owner && Object.keys(safeSummary.by_owner).length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-3">임대사업자별 현황</h4>
                <div className="space-y-2">
                  {Object.entries(safeSummary.by_owner).map(([owner, data]) => (
                    <div key={owner} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span className="font-medium">{owner}</span>
                      <div className="flex space-x-4">
                        <span>건물: {data.buildings || 0}개</span>
                        <span>세대: {data.total_units || 0}개</span>
                        <span>공실: {data.vacant_units || 0}개</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'tenants':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">총 임차인 수</h4>
                <p className="text-2xl font-bold text-green-600">{safeSummary.total_tenants || 0}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">신규 임차인</h4>
                <p className="text-2xl font-bold text-blue-600">{safeSummary.new_this_month || 0}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-900">퇴거 임차인</h4>
                <p className="text-2xl font-bold text-red-600">{safeSummary.departing_this_month || 0}</p>
              </div>
            </div>
          </div>
        );

      case 'contracts':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">활성 계약</h4>
                <p className="text-2xl font-bold text-blue-600">{safeSummary.total_contracts || 0}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900">만료 예정</h4>
                <p className="text-2xl font-bold text-orange-600">{safeSummary.expiring_soon || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">갱신율</h4>
                <p className="text-2xl font-bold text-green-600">{safeSummary.renewal_rate || 0}%</p>
              </div>
            </div>
          </div>
        );

      case 'financial':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">월 수입</h4>
                <p className="text-2xl font-bold text-green-600">{(safeSummary.monthly_revenue || 0).toLocaleString()}원</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-900">월 지출</h4>
                <p className="text-2xl font-bold text-red-600">{(safeSummary.monthly_expenses || 0).toLocaleString()}원</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">순이익</h4>
                <p className="text-2xl font-bold text-blue-600">{(safeSummary.net_profit || 0).toLocaleString()}원</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900">마진율</h4>
                <p className="text-2xl font-bold text-purple-600">{safeSummary.profit_margin || 0}%</p>
              </div>
            </div>
          </div>
        );

      case 'maintenance':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">총 요청</h4>
                <p className="text-2xl font-bold text-blue-600">{safeSummary.total_requests || 0}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900">대기 중</h4>
                <p className="text-2xl font-bold text-orange-600">{safeSummary.pending || 0}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-900">진행 중</h4>
                <p className="text-2xl font-bold text-yellow-600">{safeSummary.in_progress || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">완료</h4>
                <p className="text-2xl font-bold text-green-600">{safeSummary.completed || 0}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900">완료율</h4>
                <p className="text-2xl font-bold text-purple-600">{safeSummary.completion_rate || 0}%</p>
              </div>
            </div>
          </div>
        );

      case 'vacancy':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900">공실 세대</h4>
                <p className="text-2xl font-bold text-orange-600">{safeSummary.total_vacant_units || 0}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-900">공실율</h4>
                <p className="text-2xl font-bold text-red-600">{safeSummary.vacancy_rate || 0}%</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900">평균 공실 기간</h4>
                <p className="text-2xl font-bold text-blue-600">{safeSummary.avg_duration || 0}일</p>
              </div>
            </div>
          </div>
        );

      case 'overdue':
        return (
          <div className="space-y-4">
            {/* 연체 현황 테이블 */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h4 className="font-semibold text-gray-900">연체 현황</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        임대사업자
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        총 발행 대상 청구건수
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        발행 청구건수
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        미발행 청구건수
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        미납 청구건수
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* 전체 합계 행 */}
                    <tr className="bg-blue-50 font-semibold">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                        전체
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                        {safeSummary.total_billing_target || 0}건
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                        {safeSummary.issued_billing || 0}건
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                        {safeSummary.not_issued_billing || 0}건
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                        {safeSummary.issued_not_paid || 0}건
                      </td>
                    </tr>
                    
                    {/* 임대사업자별 상세 */}
                    {safeSummary.by_owner && Object.keys(safeSummary.by_owner).length > 0 && 
                      Object.entries(safeSummary.by_owner).map(([owner, data]) => (
                        <tr key={owner} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {owner}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {data.total_billing_target || 0}건
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {data.issued_billing || 0}건
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {data.not_issued_billing || 0}건
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {data.issued_not_paid || 0}건
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="text-gray-500">데이터가 없습니다.</div>;
    }
  };

  const renderDetailedTable = (businessType) => {
    if (!detailedData) {
      return <div className="text-gray-500">데이터를 불러오는 중...</div>;
    }

    return (
      <div className="space-y-4">
        {/* 검색 및 필터 */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="검색..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* 테이블 */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {renderTableHeader(businessType)}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {detailedData.map((item, index) => renderTableRow(businessType, item, index))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {pagination && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              총 {pagination.total}개 중 {((pagination.page - 1) * pagination.per_page) + 1}-{Math.min(pagination.page * pagination.per_page, pagination.total)}개 표시
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTableHeader = (businessType) => {
    const headers = {
      buildings: ['건물명', '주소', '임대사업자', '총 세대', '점유 세대', '공실 세대', '점유율'],
      tenants: ['임차인명', '연락처', '이메일', '현재 건물', '현재 세대', '임대사업자', '월 임대료'],
      contracts: ['임차인', '건물', '세대', '임대사업자', '계약 시작', '계약 종료', '월 임대료', '상태'],
      financial: ['청구월', '청구서번호', '임차인', '건물/세대', '임대사업자', '청구액', '입금액', '미수금', '상태'],
      maintenance: ['제목', '건물', '세대', '임대사업자', '상태', '우선순위', '요청일', '완료일'],
      vacancy: ['건물', '세대', '임대사업자', '세대 유형', '면적', '월 임대료', '마지막 임차인', '공실 기간'],
      overdue: ['임차인', '건물', '세대', '임대사업자', '납부기한', '연체금액', '연체일수', '연체단계', '상태']
    };

    return (
      <tr>
        {headers[businessType]?.map((header, index) => (
          <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {header}
          </th>
        ))}
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          액션
        </th>
      </tr>
    );
  };

  const renderTableRow = (businessType, item, index) => {
    switch (businessType) {
      case 'buildings':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.address}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.total_units}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.occupied_units}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.vacant_units}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.occupancy_rate}%
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'tenants':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.phone}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.email}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.current_building}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.current_unit}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.rent_amount?.toLocaleString()}만원
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'contracts':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.tenant_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.building_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.unit_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.start_date}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.end_date}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.rent_amount?.toLocaleString()}만원
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                item.is_expiring_soon ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'maintenance':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.title}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.building_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.unit_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                item.status === 'completed' ? 'bg-green-100 text-green-800' :
                item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.priority}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.requested_date}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.completed_date || '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'financial':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.billing_month}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.statement_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.tenant_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.building_name} / {item.unit_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {Number(item.billed_amount || 0).toLocaleString()}원
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
              {Number(item.paid_amount || 0).toLocaleString()}원
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
              {Number(item.outstanding_amount || 0).toLocaleString()}원
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                item.status === 'paid' ? 'bg-green-100 text-green-800' :
                item.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                item.status === 'overdue' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'vacancy':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.building_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.unit_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.unit_type}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.area}㎡
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.rent_amount?.toLocaleString()}만원
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.last_tenant || '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.vacancy_duration}일
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button className="text-blue-600 hover:text-blue-900">상세</button>
            </td>
          </tr>
        );

      case 'overdue':
        return (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.tenant_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.building_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.unit_number}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.property_owner_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.due_date}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.overdue_amount?.toLocaleString()}원
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.overdue_days}일
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.overdue_stage}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                item.overdue_status === 'green' ? 'bg-green-100 text-green-800' :
                item.overdue_status === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                item.overdue_status === 'orange' ? 'bg-orange-100 text-orange-800' :
                item.overdue_status === 'red' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {item.overdue_status}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div className="flex space-x-2">
                <button className="text-blue-600 hover:text-blue-900">상세</button>
                {activeTab === 'overdue' && (
                  <>
                    <button 
                      onClick={() => handleSendNotification(item.id)}
                      className="text-green-600 hover:text-green-900"
                    >
                      알림
                    </button>
                    <button 
                      onClick={() => handleUpdateStage(item.id)}
                      className="text-orange-600 hover:text-orange-900"
                    >
                      단계변경
                    </button>
                    <button 
                      onClick={() => handleResolveOverdue(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      해결
                    </button>
                  </>
                )}
              </div>
            </td>
          </tr>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">오류 발생</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button 
            onClick={() => {
              setError(null);
              fetchIntegratedStats();
              fetchPropertyOwners();
              fetchOverdueSummary();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: '전체 현황', icon: BarChart3 },
    { id: 'buildings', name: '건물/세대', icon: Building2 },
    { id: 'tenants', name: '임차인', icon: Users },
    { id: 'contracts', name: '계약', icon: FileText },
    { id: 'financial', name: '재무', icon: DollarSign },
    { id: 'maintenance', name: '유지보수', icon: Wrench },
    { id: 'vacancy', name: '공실', icon: Home },
    { id: 'overdue', name: '연체', icon: Clock }
  ];

  return (
    <div className="space-y-6">
      
      {/* 헤더 */}
             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
               <div>
                 <h1 className="text-xl sm:text-2xl font-bold text-gray-900">통합 관리 대시보드</h1>
                 <p className="text-sm sm:text-base text-gray-600">소속 임대사업자들의 전체 현황을 한눈에 확인하세요</p>
               </div>
               <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                 {/* 임대사업자 선택 드롭다운 */}
                 <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                   <label className="text-xs sm:text-sm font-medium text-gray-700">임대사업자:</label>
                   <select
                     value={selectedOwners.length > 0 ? selectedOwners[0] : ''}
                     onChange={(e) => {
                       if (e.target.value) {
                         setSelectedOwners([e.target.value]);
                       } else {
                         setSelectedOwners([]);
                       }
                     }}
                     className="px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0 text-xs sm:text-sm"
                   >
                     <option value="">전체 임대사업자</option>
                     {propertyOwners.map((owner) => (
                       <option key={owner.id} value={owner.name}>
                         {activeTab === 'maintenance'
                           ? `${owner.name} (${owner.maintenance_requests_count || 0}건 신청)`
                           : `${owner.name} (${owner.buildings_count}개 건물)`}
                       </option>
                     ))}
                   </select>
                 </div>
                 
               </div>
             </div>

      {/* 업무별 탭 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center flex-shrink-0`}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name.charAt(0)}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>


      {/* 탭별 콘텐츠 */}
      {activeTab === 'overview' && (
        <>
          {/* 통계 카드 */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            <StatCard
              title="총 임대사업자"
              value={stats?.total_property_owners}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="총 건물 수"
              value={stats?.total_buildings}
              icon={Building2}
              color="green"
            />
            <StatCard
              title="총 세대 수"
              value={stats?.total_units}
              icon={FileText}
              color="purple"
            />
            <StatCard
              title="총 계약 수"
              value={stats?.total_contracts}
              icon={FileText}
              color="indigo"
            />
          </div>

          {/* 수입 및 연체 현황 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">수입 현황</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">이번 달 수입</span>
                  <span className="text-lg sm:text-2xl font-bold text-green-600">
                    {stats?.monthly_revenue?.toLocaleString() || 0}원
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">연체율</span>
                  <span className={`text-base sm:text-lg font-semibold ${stats?.overdue_rate > 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {stats?.overdue_rate || 0}%
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">연체 현황</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">총 연체 건수</span>
                  <span className="text-lg sm:text-2xl font-bold text-red-600">
                    {businessSummary?.overdue?.total || 0}건
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">공실 세대</span>
                  <span className="text-base sm:text-lg font-semibold text-orange-600">
                    {businessSummary?.vacancy?.total || 0}세대
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 업무별 상세 콘텐츠 */}
      {activeTab !== 'overview' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {tabs.find(tab => tab.id === activeTab)?.name} 상세 현황
            </h3>
            {/* 상세 API가 있는 탭에서만 상세보기 버튼 표시 */}
            {activeTab !== 'overdue' && (
              <button
                onClick={toggleDetailedView}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {showDetailedView ? '요약 보기' : '상세 보기'}
              </button>
            )}
          </div>
          
          {/* 연체 탭에서는 항상 요약 보기만 표시 */}
          {activeTab === 'overdue' ? (
            renderBusinessContent(activeTab)
          ) : !showDetailedView ? (
            renderBusinessContent(activeTab)
          ) : (
            renderDetailedTable(activeTab)
          )}
        </div>
      )}


    </div>
  );
};

export default RMCDashboard;
